import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import { Link } from "react-router-dom";

function Lightning94() {
  const contentRef = useRef(null);
  const titleText = "Atomic Payments and Penalty Mechanisms";
  const questions = [
    {
      text: "1. What is the purpose of a funding transaction in a payment channel?",
      options: [
        {
          value: "A",
          label: "A. To close the channel and distribute funds",
        },
        {
          value: "B",
          label: "B. To lock shared funds into a 2-of-2 multisignature output",
        },
        {
          value: "C",
          label: "C. To generate routing fees",
        },
        {
          value: "D",
          label: "D. To create an invoice",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What prevents a participant from broadcasting an old commitment transaction to cheat?",
      options: [
        { value: "A", label: "A. A time delay on all transactions" },
        {
          value: "B",
          label:
            "B. A special revocation mechanism that allows the other party to claim all funds",
        },
        {
          value: "C",
          label: "C. A central server that monitors updates",
        },
        {
          value: "D",
          label: "D. A fee penalty",
        },
      ],
      correct: "B",
    },
    {
      text: "3. What allows Amina and Bilal to update balances without broadcasting each update on-chain?",
      options: [
        {
          value: "A",
          label: "A. They use a custodial wallet",
        },
        {
          value: "B",
          label:
            "B. They create new commitment transactions that replace the old ones",
        },
        {
          value: "C",
          label: "C. They send updates through miners",
        },
        {
          value: "D",
          label: "D. They store updates in a blockchain archive",
        },
      ],
      correct: "B",
    },
    {
      text: "4. What happens if one party disappears before the channel is closed cooperatively?",
      options: [
        { value: "A", label: "A. The funds are lost forever" },
        {
          value: "B",
          label: "B. The channel remains open permanently",
        },
        {
          value: "C",
          label:
            "C. The remaining party can use the latest commitment transaction to close the channel",
        },
        {
          value: "D",
          label: "D. The channel is frozen by the Lightning Network",
        },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={94}
        timerMinutes={60}
        currentLevel={7}
      />
      <p className="content">
        Welcome to <b>Day 94</b>! and the fourth day of your Lightning Network
        learning journey. Today we explore the inner workings of Bitcoin payment
        channels which form the backbone of the Lightning Network’s ability to
        process fast, low cost and scalable transactions. By following the story
        of Amina and Bilal, you will understand how channels are created,
        updated and closed, and how Bitcoin’s rules ensure fairness and security
        between two people transacting off chain. This lesson builds your
        foundation for understanding more advanced concepts in the Lightning
        Network.
        <br />
        <br />
        <b>Recap: What Is a Payment Channel</b>
        <br />
        <br />
        A payment channel is the foundational building block of the Lightning
        Network and serves as the mechanism through which two participants can
        transact with each other instantly and without broadcasting every
        transaction to the Bitcoin blockchain. To understand how this works in a
        simple way, imagine that Amina and Bilal want to exchange bitcoin many
        times over the course of a week. If every payment between them had to be
        broadcast to the blockchain, it would be slow, expensive, and burdensome
        for the network because Bitcoin blocks are mined roughly every ten
        minutes and space in each block is limited. The Lightning Network solves
        this by letting Amina and Bilal keep a private tab off chain. They only
        need to make one transaction on chain when they start the relationship
        and another one when they end it. All the activity between those two
        moments happens privately through cryptographic updates that do not
        touch the blockchain until they are needed. This approach brings
        tremendous efficiency and scalability to Bitcoin while preserving the
        security of the base layer.
        <br />
        In its simplest form, a payment channel begins with a funding
        transaction. Amina and Bilal decide how much bitcoin they want to lock
        into a shared two of two multisignature address. This means both of them
        must cooperate to spend the funds. They create a transaction that sends
        bitcoin into this multisignature output and the transaction is broadcast
        to the blockchain. Once it confirms, the funding step is complete and
        the channel is considered open. It is important to note that although
        the funds appear locked, they are not inaccessible. They will be
        returned to one or both participants when the channel is eventually
        closed. For now, the funds simply sit there as collateral that backs
        every off chain transaction the two of them will make.
        <br />
        Once the funding transaction is confirmed, Amina and Bilal create what
        is known as a commitment transaction. This commitment transaction
        represents the state of the payment channel at a particular moment. It
        describes how the funds should be divided between them if the channel
        were to close immediately. At the start, since the bitcoin was deposited
        in the way both agreed, the first commitment transaction reflects their
        initial balances. If Amina contributed 0.05 bitcoin and Bilal
        contributed 0.05 bitcoin, the commitment transaction would show that
        both have 0.05. If only one contributed, then that single contributor
        would hold the entire balance at the beginning. What matters is that the
        commitment transaction defines the ownership at each step of the
        channel.
        <br />
        The beauty of the Lightning Network is that Amina and Bilal can update
        this commitment transaction privately as many times as they wish. They
        do not need to broadcast these updates to the blockchain. If Amina pays
        Bilal some sats, they simply create a new commitment transaction that
        reflects the updated balance. The old one becomes outdated and the new
        one becomes the current truth between them. This is what allows
        Lightning payments to be instant and practically limitless in frequency.
        They can transact ten times per minute or a hundred times per hour and
        the blockchain remains untouched.
        <br />
        The channel now acts like a secure envelope that contains a continuously
        updated record of who owns what. As long as Amina and Bilal behave
        honestly, the process is smooth and efficient. When either of them
        decides they no longer want to transact or they wish to settle their
        balances on-chain, they close the channel. This results in one on-chain
        transaction that uses the latest commitment state to distribute the
        funds. The entire series of private exchanges is compressed into two
        on-chain events. This makes Lightning extraordinarily powerful for
        scaling Bitcoin since millions of microtransactions can occur without
        burdening the blockchain.
        <br />
        Payment channels carry several important benefits. They dramatically
        reduce fees because most activity happens off chain where there is no
        miner fee for each payment. They increase privacy because the
        intermediate channel updates are not visible on the global ledger. They
        improve speed as updates are exchanged directly between the participants
        and are settled instantly. They allow Bitcoin to function as a daily
        medium of exchange while preserving the robustness of the base layer as
        the ultimate settlement layer. Throughout this lesson we will explore
        how the channel works internally, how it prevents cheating, how it
        routes payments across participants who are not directly connected, and
        how it ultimately closes in a secure and predictable way.
        <br />
        <br />
        <b>Updating Channel State and Ensuring Safety</b>
        <br />
        <br />
        The heart of the Lightning Network lies in its ability to update channel
        states securely and instantly without needing to involve the blockchain.
        This requires a robust system for maintaining fairness and preventing
        either participant from acting dishonestly. To understand this better,
        let us return to Amina and Bilal. They have opened their payment channel
        and funded it. Their initial commitment transaction accurately reflects
        the amount of bitcoin each has within the channel. Now they want to
        exchange small payments back and forth. Each time Amina pays Bilal or
        Bilal pays Amina, they create a new version of the commitment
        transaction that represents the latest distribution of funds.
        <br />
        However, this flexibility creates a challenge. What if one participant
        tries to cheat by broadcasting an old commitment transaction that gives
        them a larger balance than they currently deserve? For example, imagine
        Amina has paid Bilal several times. The earlier commitment transaction
        still shows she has more bitcoin than she does now. If she broadcasts
        that old transaction, she might try to steal funds. The Lightning
        Network solves this problem using a clever system of revocation secrets
        and penalty transactions. Every time Amina and Bilal create a new
        commitment transaction, they also exchange a secret that invalidates the
        previous version. This secret essentially acts as a trap. If one of them
        attempts to cheat by broadcasting an outdated transaction, the other can
        use the revocation secret to claim the entire channel balance as a
        penalty. This makes cheating extremely expensive and therefore
        irrational.
        <br />
        Commitment transactions are designed in a special way. They contain
        timelocked outputs. Each participant holds their own copy of the
        commitment transaction that pays them in a safe and predictable manner,
        but if they attempt to misuse a past version, the other participant has
        the tools to punish them. Lightning channels rely heavily on this
        incentive design. Honest behavior is always the easiest and safest path.
        Cheating is technically possible but economically suicidal. This
        equilibrium encourages cooperation and trustless interaction without the
        need for a central authority.
        <br />
        Let us walk through an example. Suppose the channel now has 0.1 bitcoin
        total. Amina initially held 0.06 and Bilal held 0.04. After several
        payments, the balance has shifted so that Amina now has 0.03 and Bilal
        has 0.07. Their latest commitment transaction reflects this. The
        previous version, which shows Amina with 0.06, is now obsolete. Amina
        must never broadcast that older version. Bilal holds a revocation secret
        that allows him to penalize her if she tries. This threat ensures Amina
        behaves honestly.
        <br />
        This system is essential for the Lightning Network to function securely.
        Payments on Lightning are fully trustless, but they depend on
        participants staying online to monitor the blockchain for dishonest
        closures. If Amina were offline for a long time, Bilal could attempt to
        cheat by publishing an outdated state. To protect against these
        situations, Lightning employs watchtowers. A watchtower is a service
        that monitors the blockchain on behalf of users. If it detects a
        cheating attempt, it can use pre-provided encrypted data to broadcast a
        penalty transaction. This additional layer of protection ensures that
        even users who cannot be online constantly can still engage in Lightning
        payments safely.
        <br />
        Updating channel state also allows for dynamic and flexible payment
        flow. Rates, balances, and liquidity shift with each update. Lightning
        channels continue to operate smoothly as long as both participants
        exchange signatures, commitments, and revocation secrets. The entire
        process is built on cooperation but enforces honesty through
        cryptographic mechanisms and economic incentives. Over time, Amina and
        Bilal accumulate a long chain of updates, but only the latest one
        matters since Lightning always considers the newest state as the
        authoritative version.
        <br />
        These updates happen extremely quickly. The delay is mostly limited by
        network latency and the ability of the two devices to exchange
        signatures. This makes Lightning suitable for real-time applications
        like buying coffee, paying for services online, gaming, streaming, or
        sending micropayments in rapid succession. The channel is like a living
        document that both Amina and Bilal continually revise. They sign each
        revision, discard the previous ones, and move forward with a shared
        understanding of the current state.
        <br />
        This internal updating process is one of the reasons Lightning is viewed
        as a natural scaling solution for Bitcoin. Instead of every update being
        carved into a global ledger, channel states remain private and ephemeral
        until needed for final settlement. The blockchain is used for occasional
        anchor transactions and for protecting against disputes but is not
        involved in everyday interactions. This preserves Bitcoin’s
        decentralization and efficiency while enabling widespread use for small,
        frequent payments.
        <br />
        <br />
        <b> Connecting Channels and Routing Payments</b>
        <br />
        <br />
        The Lightning Network is more than just a collection of isolated payment
        channels. It is a vast, interconnected graph where each channel links to
        others, allowing payments to move across the network even when the
        sender and receiver do not share a direct channel. This is where multi
        hop routing becomes essential. Even if Amina and Bilal have a channel,
        and Bilal has a channel with Careem, Amina can send a payment to Careem
        through Bilal. The magic that makes this safe and atomic is the Hashed
        Timelock Contract, also known as the HTLC.
        <br />
        Let us imagine that Careem wants to receive a payment from Amina. Amina
        has no direct channel with Careem. She only has a channel with Bilal.
        Fortunately, Bilal has a channel with Careem. The network uses these
        connections to deliver the payment in a secure chain. Careem generates a
        random secret and produces a hash of that secret. He gives the hash to
        Amina as part of a payment request. Amina cannot see the secret; she
        only sees the hash. Using this hash, Amina constructs an HTLC in her
        channel with Bilal. The HTLC promises Bilal that he will receive some
        sats if he can provide the preimage of the hash before a certain time
        expires. If Bilal cannot provide the secret in time, the payment is
        refunded to Amina automatically.
        <br />
        Bilal now creates a similar HTLC with Careem. He promises Careem that if
        Careem reveals the secret corresponding to the hash, he will receive the
        sats. Because Careem has the secret, he can claim the HTLC from Bilal.
        When he does so, he reveals the secret to the HTLC. This revelation
        flows backward along the payment route. Bilal receives the secret and
        uses it to claim the sats from Amina. In this way the payment is
        executed atomically. Either all steps succeed or none do. No participant
        needs to trust the other because each hop relies on cryptographic
        conditions.
        <br />
        Routing payments in Lightning often involves multiple hops. Amina might
        connect to someone who connects to someone else who eventually connects
        to Careem. The network finds a path where liquidity flows smoothly
        through all the involved channels. Liquidity is an important
        consideration because each channel has a certain balance on each side.
        If Bilal does not have enough outgoing balance in his channel with
        Careem, he cannot forward the payment. Lightning nodes typically run
        algorithms to discover viable paths that meet liquidity and fee
        constraints.
        <br />
        One of the remarkable aspects of Lightning routing is that it remains
        private. Intermediate nodes like Bilal do not know the origin or final
        destination of the payment. They only know the next hop. This is made
        possible by a technique called onion routing. Each hop receives an
        encrypted layer of information that tells it where to forward the
        payment next. When that layer is peeled away, the next instruction
        becomes visible. No hop can see the entire path, ensuring privacy for
        both sender and receiver.
        <br />
        Routing payments relies on a well connected network. As more channels
        are created, the network becomes more resilient. Even if a particular
        path fails due to insufficient liquidity, the network can attempt
        alternate routes. Payments may sometimes fail or be delayed when
        liquidity is low, but ongoing improvements in pathfinding algorithms and
        new features like multi path payments are making Lightning more robust.
        Multi path payments allow a single payment to be split up and sent
        through several channels simultaneously, which helps address liquidity
        imbalances.
        <br />
        The entire routing process is seamless from the perspective of users
        like Amina and Careem. When Amina enters the amount and presses send,
        her wallet automatically searches for routes, creates the necessary
        HTLCs, and manages the flow until the payment succeeds or fails. All the
        complexity of routing stays behind the scenes. What matters is that the
        Lightning Network can move value across a global mesh of interconnected
        channels in a trustless, decentralized manner.
        <br />
        <br />
        <b>Opening, Managing, and Closing Channels</b>
        <br />
        <br />
        The lifecycle of a Lightning channel involves multiple stages. It begins
        with channel opening, continues through the active phase of channel
        updates, and ends with closure. Understanding this full cycle is
        important for beginners because it explains how Lightning interacts with
        the Bitcoin blockchain and how the network maintains security.
        <br />
        To open a channel, Amina selects a peer to connect with. This could be
        Bilal or any Lightning node she chooses. Her wallet creates a funding
        transaction that locks her chosen amount of bitcoin into a
        multisignature output that requires cooperation to spend. This funding
        transaction is broadcast to the blockchain. Once it confirms, the
        channel becomes active. From this moment forward, she and Bilal can
        exchange an unlimited number of off chain updates.
        <br />
        Managing the channel involves sending and receiving payments, updating
        balances, and keeping the wallet online or protected by a watchtower.
        Amina’s wallet stores the latest commitment transaction and the
        revocation secrets that invalidate older ones. If she receives payments,
        her balance inside the channel increases. If she sends payments, her
        balance decreases. Liquidity management is essential because she can
        only send as many sats as she has available on her side of the channel.
        If her sending capacity depletes, she may need to rebalance the channel
        or open additional channels.
        <br />
        Eventually, either Amina or Bilal may decide they want to close the
        channel. There are two ways to do this. The first is a cooperative
        close. In a cooperative close, both participants agree on the final
        distribution of funds and sign a closing transaction that spends the
        multisignature output. This is simple, clean, and efficient. The closing
        transaction usually has lower fees and is broadcast directly to the
        blockchain. After it confirms, each participant receives their final
        share of the bitcoin.
        <br />
        The second method is a unilateral close. This happens when one party
        broadcasts their copy of the latest commitment transaction without
        cooperation from the other. This may happen if the other participant is
        offline or unresponsive. A unilateral close takes longer because the
        commitment transaction often includes timelocks. The closing party must
        wait for the timelock to expire before accessing their funds. However,
        it preserves safety because the latest state is enforced by the Bitcoin
        blockchain. If a participant attempts to broadcast an outdated state,
        the revocation mechanism allows the counterparty to seize all funds in
        the channel.
        <br />
        In rare cases, disputes may occur. Suppose Bilal attempts to cheat by
        publishing an outdated commitment transaction. Because Amina possesses
        the revocation secret that corresponds to that old state, she can use a
        penalty transaction to claim the entire channel balance. This harsh
        penalty makes it extremely unlikely that anyone will attempt such
        deception. The rules are clear, the system is predictable, and the
        economic incentives guide everyone toward cooperation.
        <br />
        The channel closure marks the end of the lifecycle. After closure, both
        participants have their funds back on the blockchain. They can use them
        to open new channels with new peers or keep them in cold storage for
        long term savings. The Lightning Network is flexible and allows users to
        engage with it to whatever extent they prefer. Some may keep channels
        open for years while others open and close frequently depending on need.
        The important lesson is that channels give users full control over how
        they participate in this powerful second layer of Bitcoin.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today we learned how payment channels allow two users like Amina and
        Bilal to transact privately, instantly and repeatedly without recording
        every transaction on the blockchain. We explored how an initial funding
        transaction locks funds into a shared output and how commitment
        transactions constantly update the balance between the participants. We
        saw how revocation secrets prevent cheating and ensure fairness, while
        spending conditions and time based rules guarantee that either
        participant can always recover their funds even if the other disappears.
        Overall, payment channels give Lightning its speed and efficiency while
        still relying on Bitcoin’s security guarantees.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <div className="button-container">
        <Link to="/economics">
          <button className="page-button">Go Back</button>
        </Link>
        <Link to="/index-economics">
          <button className="page-button">Index</button>
        </Link>
        <Link to="/economics">
          <button className="page-button">Continue</button>
        </Link>
      </div>
    </div>
  );
}

export default Lightning94;
